import { useState } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { Text } from '@/components'
import { RatePeriod } from '@/types/plugSessions'
import RealTimeRateChart, { REAL_TIME_PLOT_LEFT } from './RealTimeRateChart'
import { formatHourRange } from './ChargeSessionDetails.utils'
import {
  formatCents,
  RATE_PERIOD_LABELS,
  UpcomingRates,
} from './UpcomingRatesCard.utils'

// Same rate palette as HourlyRateChart. The off-peak #00ab32 and peak #fb5842
// only exist as base-* colors (themed green-700 and red-500 are different hues)
export const RATE_PERIOD_BACKGROUNDS: Record<RatePeriod, string> = {
  off_peak: 'bg-base-green-700',
  flat: 'bg-base-green-700',
  negative: 'bg-base-green-700',
  low: 'bg-base-green-700',
  partial_peak: 'bg-themed-orange-400',
  medium: 'bg-themed-orange-400',
  peak: 'bg-base-red-500',
  high: 'bg-base-red-500',
}

type Props = {
  id?: string
  rates: UpcomingRates
  // Storybook only: open the readout on one segment or hour
  activeSegment?: number | null
}

/** The tier, price and hour range the title row reads out for a selection. */
function getActiveRate(rates: UpcomingRates, active: number | null) {
  if (active === null) return null
  if (rates.hourlyBars) {
    const bar = rates.hourlyBars[active]
    if (!bar) return null
    return {
      period: bar.period,
      cents: bar.cents,
      start: bar.start,
      end: dayjs(bar.start).add(1, 'hour').toISOString(),
    }
  }
  return rates.segments[active] ?? null
}

export default function UpcomingRatesCard({
  id,
  rates,
  activeSegment = null,
}: Props) {
  const lastIndex = rates.segments.length - 1
  // Hover on desktop, tap on the phone: both land here. Indexes a segment of
  // the TOU bar, or an hour of the real-time chart
  const [active, setActive] = useState<number | null>(activeSegment)
  const activeRate = getActiveRate(rates, active)

  return (
    <section
      id={id}
      aria-label={rates.title}
      className="box-border flex flex-col w-full gap-4 p-6 overflow-hidden bg-white rounded-[20px] shadow-spread"
    >
      {/* Two lines tall even when empty: the readout appearing must not move
          the bar, or the pointer loses the segment and the readout flickers */}
      <div className="flex items-start justify-between gap-3 min-h-[34px]">
        <Text variant="body1">{rates.title}</Text>
        {activeRate && (
          <span
            role="status"
            className="text-[11px] font-bold leading-4 text-right text-themed-grey-900 whitespace-nowrap"
          >
            {RATE_PERIOD_LABELS[activeRate.period]} ·{' '}
            {formatCents(activeRate.cents)}/kWh
            <span className="block text-[10px] font-medium text-themed-grey-500">
              {formatHourRange(dayjs(activeRate.start), dayjs(activeRate.end))}
            </span>
          </span>
        )}
      </div>
      {rates.hourlyBars && rates.yAxisTicksCents ? (
        <RealTimeRateChart
          bars={rates.hourlyBars}
          yAxisTicksCents={rates.yAxisTicksCents}
          axisLabels={rates.axisLabels}
          active={active}
          onActiveChange={(index) => setActive(index ?? activeSegment)}
        />
      ) : (
        <div className="flex flex-col w-full gap-1.5">
          <div
            className="flex w-full h-3 gap-[2px]"
            onMouseLeave={() => setActive(activeSegment)}
          >
            {rates.segments.map((segment, index) => (
              <button
                key={`${segment.start}-${index}`}
                type="button"
                aria-label={`${
                  RATE_PERIOD_LABELS[segment.period]
                }, ${formatCents(segment.cents)} per kWh, ${formatHourRange(
                  dayjs(segment.start),
                  dayjs(segment.end),
                )}`}
                aria-pressed={active === index}
                onMouseEnter={() => setActive(index)}
                onFocus={() => setActive(index)}
                onBlur={() => setActive(activeSegment)}
                // Focus already opened it on a tap; a click only ever opens,
                // and tapping elsewhere (blur) closes
                onClick={() => setActive(index)}
                className={clsx(
                  'h-3 min-w-[2px] p-0 border-0 rounded-[3px] cursor-pointer transition-opacity',
                  RATE_PERIOD_BACKGROUNDS[segment.period],
                  index === 0 && 'rounded-l-md',
                  index === lastIndex && 'rounded-r-md',
                  active !== null && active !== index && 'opacity-60',
                )}
                style={{ flexGrow: segment.weight, flexBasis: 0 }}
              />
            ))}
          </div>
          <div className="flex justify-between w-full text-[10.886px] font-bold text-themed-grey-500">
            {rates.axisLabels.map((label, index) => (
              <span key={`${label}-${index}`}>{label}</span>
            ))}
          </div>
        </div>
      )}
      {/* Legend lines up with the left edge of the graph or bar, 10px between
          entries. The hourly chart's plot starts after its ¢ labels. */}
      <div
        className="box-border flex flex-wrap justify-start w-full gap-[10px]"
        style={{ paddingLeft: rates.hourlyBars ? REAL_TIME_PLOT_LEFT : 0 }}
      >
        {rates.legend.map((item) => (
          <div key={item.title} className="flex items-start gap-[3px]">
            <span
              aria-hidden
              className={clsx(
                'mt-1 w-[6px] h-[6px] rounded-full shrink-0',
                RATE_PERIOD_BACKGROUNDS[item.period],
              )}
            />
            <div className="flex flex-col gap-[2px] text-[9.442px] leading-[15.7px]">
              <span className="font-bold text-themed-grey-900">
                {item.title}
              </span>
              <span className="font-medium text-themed-grey-500">
                {item.detail}
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
